import moment from "moment";
import { getMoment, formatTimeDifference as sharedFormatTimeDifference, formatNights as sharedFormatNights, type TimeDifferenceStrings } from "shared/utils/momentutils";
import { DayOfWeekMonthDayShort, DayOfWeekMonthDayYear, DayOfWeekMonthDayYearShort } from "shared/utils/momentutils.customformats";

type DateTimeLikeObject = moment.Moment | Date | string;

// The locale aware, non localized date/time formatters now live in the shared project.
export {
  is24Hour,
  firstDayOfWeek,
  timeSeparator,
  formatDayWithDate,
  formatShortDate,
  formatBirthdateWithAge,
  formatLongDate,
  formatLongDateWithTime,
  formatShortDateWithTime,
  formatTime,
  formatWeekDay,
  formatShortDateAndTime as fromatShortDateAndTime,
  formatWrittenDateTimeUtc,
  formatWrittenDay,
  formatWrittenDayMonth,
  formatWrittenDayMonthYear,
  formatWrittenDayMonthYearShort,
  formatShortDateWithTimeZone,
  parseFromTime,
  parseFromTimeString,
  getNumberOfNights,
  systemTimezone
} from "shared/utils/momentutils";

// The formatters below build localized sentences from RESX, which the shared project cannot access, so they stay here.

const formatDaysSince = (m: DateTimeLikeObject): string => {
  const localMoment = moment(site.localNow);
  const daysSince = localMoment.diff(getMoment(m), "days");

  return daysSince === 0
    ? RESX.DateTime.Today
    : daysSince === 1
      ? RESX.DateTime.YDay
      : `${daysSince.toString()} ${RESX.DateTime.days}`;
}

const formatOverDue = (days: number) => {
  if (days < -1) return `${RESX.DateTime.lblIn} ${(-days)} ${RESX.DateTime.days}`;
  if (days === -1) return `${RESX.DateTime.lblIn} ${RESX.DateTime.YDay}`;
  if (days === 0) return RESX.DateTime.Today;
  if (days === 1) return RESX.DateTime.YDayOverDue;
  if (days > 1) return RESX.DateTime.XDaysOverDue.replace("{0}", days.toString());
};

const formatMonths = (months: number): string => {
  if (months === 1) {
    return RESX.DateTime.YMonth;
  }

  return RESX.DateTime.XMonths.replace("{0}", months.toString());
}

// Collects the localized duration words from RESX so the shared duration
// formatters can stay free of any resource system.
export const timeDifferenceStringsFromResx = (): TimeDifferenceStrings => ({
  YYear: RESX.DateTime.YYear,
  XYears: RESX.DateTime.XYears,
  YMonth: RESX.DateTime.YMonth,
  XMonths: RESX.DateTime.XMonths,
  YDay: RESX.DateTime.YDay,
  days: RESX.DateTime.days,
  YHour: RESX.DateTime.YHour,
  XHours: RESX.DateTime.XHours,
  YMinute: RESX.DateTime.YMinute,
  XMinutes: RESX.DateTime.XMinutes,
  YSecond: RESX.DateTime.YSecond,
  XSeconds: RESX.DateTime.XSeconds,
  night: RESX.DateTime.night,
  nights: RESX.DateTime.nights
});

// like "2 years, 5 days"
const formatTimeDifference = (start: moment.Moment, end: moment.Moment): string =>
  sharedFormatTimeDifference(start, end, timeDifferenceStringsFromResx());

const formatNights = (start: moment.Moment, end: moment.Moment): string =>
  sharedFormatNights(start, end, timeDifferenceStringsFromResx());

// Ensures every interface culture has a custom format configured. Runs here because it needs serverReference.
const customFormatsToValidate = [DayOfWeekMonthDayShort, DayOfWeekMonthDayYear, DayOfWeekMonthDayYearShort];
customFormatsToValidate.forEach(format => {
  serverReference.interfaceCultures.forEach(culture => {
    if (!format[culture.locale]) {
      console.error(`Locale ${culture.locale} is not configured for all formats`);
      throw new Error(`Locale ${culture.locale} is not configured for all formats`);
    }
  });
});

export {
  formatDaysSince,
  formatOverDue,
  formatMonths,
  formatTimeDifference,
  formatNights
};
